import { useEffect, useState } from "react";
import { fetchPromotionReadiness } from "../lib/api";
import { CheckCircle2, XCircle, Info, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

/**
 * ReadinessBoard — Detailed criterion checklist for a target role.
 * Shows each criterion, current vs target value, met status, and offers an "Add missing → IDP" CTA.
 */
export default function ReadinessBoard({ employeeId, targetRoleId, onAddToIdp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetRoleId) return;
    setLoading(true);
    fetchPromotionReadiness(employeeId, targetRoleId).then(setData).finally(() => setLoading(false));
  }, [employeeId, targetRoleId]);

  if (!targetRoleId) return null;
  if (loading) return <div className="card-surface p-6 text-sm text-slate-400" data-testid="readiness-loading">Hazırlık analizi yükleniyor…</div>;
  if (!data) return null;

  const missing = (data.checks || []).filter((c) => !c.met);
  const met = (data.checks || []).filter((c) => c.met);

  return (
    <div className="card-surface p-6" data-testid="readiness-board">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label-eyebrow mb-1">Terfi Hazırlık Analizi</div>
          <h3 className="font-display text-xl font-semibold">{data.role_title}</h3>
          <p className="text-xs text-slate-500 mt-1">
            {data.mandatory_met}/{data.mandatory_total} zorunlu · {data.criteria_met}/{data.criteria_total} toplam kriter karşılandı
          </p>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold ${data.can_promote ? "text-emerald-600" : "text-amber-600"}`}>%{data.readiness_pct}</div>
          <div className={`text-[11px] uppercase tracking-wider font-semibold ${data.can_promote ? "text-emerald-700" : "text-amber-700"}`}>
            {data.can_promote ? "Terfiye Hazır" : "Gelişim Sürüyor"}
          </div>
        </div>
      </div>

      {/* Missing criteria first */}
      {missing.length > 0 && (
        <div className="mb-5">
          <div className="text-xs uppercase tracking-wider font-semibold text-rose-600 mb-2">Eksik Kriterler ({missing.length})</div>
          <div className="space-y-2">
            {missing.map((c) => (
              <CritRow key={c.id} c={c} onAddToIdp={onAddToIdp} employeeId={employeeId} />
            ))}
          </div>
        </div>
      )}
      {met.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-emerald-600 mb-2">Karşılananlar ({met.length})</div>
          <div className="space-y-1.5">
            {met.map((c) => (
              <CritRow key={c.id} c={c} compact />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CritRow({ c, compact, onAddToIdp, employeeId }) {
  return (
    <div className={`flex items-center gap-3 p-2 rounded-lg ${c.met ? "bg-emerald-50/60" : "bg-rose-50/60 border border-rose-100"}`} data-testid={`readiness-check-${c.id}`}>
      {c.met ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-rose-500 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate">
          {c.label}
          {c.mandatory && <span className="ml-1.5 text-[10px] text-rose-600 font-semibold uppercase">zorunlu</span>}
        </div>
        {!compact && (
          <div className="text-[11px] text-slate-500">
            Mevcut: <span className="font-mono text-slate-700">{String(c.current)}</span> · Hedef: <span className="font-mono text-slate-700">{String(c.target)}</span>
          </div>
        )}
      </div>
      {!c.met && onAddToIdp && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[11px] border-sky-200 text-sky-700 hover:bg-sky-50"
          onClick={() => onAddToIdp({ criterion: c, employeeId })}
          data-testid={`readiness-add-to-idp-${c.id}`}
        >
          IDP&apos;ye ekle <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );
}
